// O seletor da origem do desembolso da obra (planning-workspace; design D8/D11), no bloco do
// topo da área de trabalho: informa a origem vigente e se ela foi ESCOLHIDA pela obra ou
// adotada como PADRÃO, e oferece a troca entre as quatro origens e a sugestão da IA.
//
// Cada opção diz se é elegível e, quando não é, o motivo — e uma opção inelegível não pode
// ser confirmada. A troca nunca é imediata: a confirmação informa o efeito (a curva é
// regenerada, regras/grupos/ajustes são mantidos, ajustes que não cabem na nova origem
// aparecem como excedentes). Cancelar não grava nada.
//
// Em modo apresentação (ou sem permissão de escrita) a origem vigente é informada e a troca
// nem é oferecida.
//
// A troca é rara, então o bloco nasce RECOLHIDO: o resumo (`<summary>`) diz a origem vigente
// e se é escolhida ou padrão, e as quatro opções só aparecem ao expandir. Uma troca pendente
// de confirmação mantém o bloco aberto, para a confirmação nunca ficar escondida.
//
// RESET (revisão de 2026-10-03): "Refazer o cronograma do zero" copia de novo a origem
// escolhida — inclusive a que já está em uso — e descarta as regras de EAP e os ajustes de
// mês (ficam no histórico); regras de insumo e grupos são mantidos. A confirmação diz
// quantos itens saem antes de qualquer gravação.
import { useState } from "react";

import { ampliInconsistentReasonLabel } from "../financialProjection/cashFlowPlanning";
import { saveDisbursementOrigin } from "./disbursementApi";

// O que cada origem traz, em uma linha, debaixo do nome da opção.
export const ORIGIN_DESCRIPTIONS = Object.freeze({
  erp_disbursement: "Cronograma de desembolso importado do ERP, serviço a serviço.",
  prevision: "Previsto aplicado na Previsão da obra.",
  physical_financial: "Previsto mensal da obra, por serviço.",
  budget: "Parte do orçamento; você distribui no tempo por nível da EAP.",
});

export const ORIGIN_ACTIONS = Object.freeze({
  erp_disbursement: "Copiar do desembolso do ERP",
  prevision: "Copiar do previsto da obra",
  physical_financial: "Copiar do Previsto",
  budget: "Criar do zero a partir do orçamento",
});

// O nome da origem como aparece em legendas e rótulos ("Segue o desembolso do ERP").
export const ORIGIN_NAMES = Object.freeze({
  erp_disbursement: "Desembolso do ERP",
  prevision: "Previsto da obra",
  physical_financial: "Previsto",
  budget: "Orçamento",
});

// Como o item que não tem regra própria é rotulado quando a origem já traz distribuição no
// tempo (D14): "Segue o desembolso do ERP" — nunca "à vista (padrão)". `OBJECT` é a mesma
// ideia como complemento de frase ("seguem o desembolso do ERP").
export const ORIGIN_FOLLOWS = Object.freeze({
  erp_disbursement: "Segue o desembolso do ERP",
  prevision: "Segue o previsto da obra",
  physical_financial: "Segue o Previsto",
  budget: "Segue o orçamento distribuído",
});

export const ORIGIN_FOLLOWS_OBJECT = Object.freeze({
  erp_disbursement: "o desembolso do ERP",
  prevision: "o previsto da obra",
  physical_financial: "o Previsto",
  budget: "o orçamento distribuído",
});

const ORDER = ["erp_disbursement", "prevision", "physical_financial", "budget"];

const REASON_FALLBACK = "Esta origem não está disponível para a obra.";

const reasonText = (reason) =>
  reason ? ampliInconsistentReasonLabel(reason) : REASON_FALLBACK;

const plural = (count, singular, pluralText) =>
  `${count} ${count === 1 ? singular : pluralText}`;

const OriginSelector = ({
  workId,
  origin,
  readOnly = false,
  onChanged,
  onAiSuggestion,
  // O que um reset descarta, para a confirmação dizer antes de gravar.
  scheduleCounts = null,
}) => {
  const [pending, setPending] = useState(undefined); // undefined = nada escolhido; null = voltar ao padrão
  const [reset, setReset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!origin) return null;

  const current = origin.id;
  const currentName = current
    ? ORIGIN_NAMES[current]
    : "Nenhuma origem elegível";
  const hasPending = pending !== undefined;
  const pendingName = pending
    ? ORIGIN_NAMES[pending]
    : "o padrão da plataforma";
  const nodeRules = scheduleCounts?.nodeRules ?? 0;
  const adjustments = scheduleCounts?.adjustments ?? 0;

  const cancel = () => {
    setPending(undefined);
    setError(null);
  };

  const toggleReset = (checked) => {
    setReset(checked);
    setError(null);
    // Desmarcar o reset com a origem em uso pendente deixaria uma "troca" para ela mesma.
    if (!checked && pending === current && origin.chosen) setPending(undefined);
  };

  const confirm = async () => {
    if (!hasPending || saving || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveDisbursementOrigin(workId, {
        origin: pending,
        ...(reset ? { reset: true } : {}),
      });
      if (result?.code === 200 || result?.code === 202) {
        setPending(undefined);
        setReset(false);
        setExpanded(false);
        await onChanged?.(result);
        return;
      }
      if (result?.errorCode === "origin_ineligible") {
        setError(reasonText(result?.details?.reason));
      } else if (result?.errorCode === "presentation_read_only") {
        setError("Obra de apresentação: a origem não pode ser alterada.");
      } else {
        setError("Não foi possível trocar a origem agora. Tente novamente.");
      }
    } catch {
      setError("Não foi possível trocar a origem agora. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const fallbackReason =
    !origin.chosen && origin.fallback?.reason && current !== "erp_disbursement"
      ? origin.fallback.reason
      : null;

  const CONFIRM_LABELS = {
    reset: { idle: "Refazer cronograma", saving: "Refazendo…" },
    switch: { idle: "Confirmar troca", saving: "Trocando…" },
  };
  const confirmLabel = CONFIRM_LABELS[reset ? "reset" : "switch"][saving ? "saving" : "idle"];

  const currentBlock = (
    <span className="ampli-origin__current">
      <span className="ampli-origin__eyebrow">Origem do desembolso</span>
      <span className="ampli-origin__name" data-testid="origin-current">
        {currentName}
      </span>
      {current ? (
        <span className="ampli-origin__mode" data-testid="origin-mode">
          {origin.chosen ? "Escolhida pela obra" : "Padrão"}
        </span>
      ) : null}
    </span>
  );

  return (
    <section className="ampli-origin" aria-label="Origem do desembolso">
      {readOnly ? <p className="ampli-origin__readonly">{currentBlock}</p> : null}
      {!readOnly ? (
        <details
          className="ampli-origin__details"
          open={expanded || hasPending}
          onToggle={(event) => {
            // Com uma troca pendente o bloco fica aberto: fechar descartaria a confirmação
            // da vista sem cancelá-la.
            if (!hasPending) setExpanded(event.currentTarget.open);
          }}
        >
          <summary className="ampli-origin__summary">
            {currentBlock}
            <span className="ampli-origin__toggle" aria-hidden="true">
              {expanded || hasPending ? "Recolher" : "Trocar ou refazer"}
            </span>
          </summary>
          <div className="ampli-origin__panel">
            <fieldset className="ampli-origin__options" disabled={saving}>
              <legend>{reset ? "Copiar de novo a partir de" : "Trocar a origem"}</legend>
              <div className="ampli-origin__grid">
                {ORDER.map((id) => {
                  const option = origin.options?.[id] ?? {
                    eligible: false,
                    reason: null,
                  };
                  const isCurrent = id === current && origin.chosen;
                  // No reset a origem em uso pode ser escolhida: é "puxar de novo" dela.
                  const locked = !option.eligible || (isCurrent && !reset);
                  const checked = hasPending ? pending === id : isCurrent && !reset;
                  return (
                    <label
                      key={id}
                      className={`ampli-origin__option${checked ? " is-checked" : ""}`}
                      data-eligible={option.eligible}
                    >
                      <input
                        type="radio"
                        name="disbursement-origin"
                        value={id}
                        checked={checked}
                        disabled={locked}
                        onChange={() => {
                          // Defesa além do `disabled`: uma opção travada nunca vira escolha
                          // pendente, qualquer que seja o caminho do evento.
                          if (locked) return;
                          setError(null);
                          setPending(id);
                        }}
                      />
                      <span className="ampli-origin__option-body">
                        <span className="ampli-origin__option-title">
                          {ORIGIN_ACTIONS[id]}
                          {id === current ? (
                            <span className="badge">
                              {origin.chosen ? "em uso" : "padrão em uso"}
                            </span>
                          ) : null}
                        </span>
                        <span className="ampli-origin__option-description">
                          {ORIGIN_DESCRIPTIONS[id]}
                        </span>
                        {!option.eligible ? (
                          <span className="ampli-origin__reason" role="note">
                            {`Indisponível: ${reasonText(option.reason)}`}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <label className={`ampli-origin__reset${reset ? " is-checked" : ""}`}>
              <input
                type="checkbox"
                checked={reset}
                disabled={saving}
                onChange={(event) => toggleReset(event.target.checked)}
              />
              <span className="ampli-origin__option-body">
                <span className="ampli-origin__option-title">
                  Refazer o cronograma do zero
                </span>
                <span className="ampli-origin__option-description">
                  {`Copia de novo a origem escolhida (pode ser a atual) e descarta ${plural(nodeRules, "regra de EAP", "regras de EAP")} e ${plural(adjustments, "ajuste de mês", "ajustes de mês")}. Regras de insumo e grupos são mantidos.`}
                </span>
              </span>
            </label>

            <div className="ampli-origin__extras">
              <button
                type="button"
                className="button button-outline small"
                onClick={() => onAiSuggestion?.()}
              >
                Sugestão da IA
              </button>
              {origin.chosen ? (
                <button
                  type="button"
                  className="button button-ghost small"
                  onClick={() => {
                    setError(null);
                    setPending(null);
                  }}
                >
                  Voltar ao padrão
                </button>
              ) : null}
            </div>

            {hasPending ? (
              <div
                className="ampli-origin__confirm"
                role="alertdialog"
                aria-label="Confirmar a troca de origem"
              >
                <p>
                  <strong>
                    {reset
                      ? `Refazer o cronograma a partir de ${pendingName}?`
                      : `Usar ${pendingName} como origem da obra?`}
                  </strong>
                </p>
                {reset ? (
                  <ul>
                    <li>{`O cronograma volta a ser uma cópia de ${pendingName}.`}</li>
                    <li>
                      {`${plural(nodeRules, "regra de EAP será descartada", "regras de EAP serão descartadas")} e ${plural(adjustments, "ajuste de mês", "ajustes de mês")} também; tudo fica no histórico.`}
                    </li>
                    <li>
                      As regras de insumo e os grupos são mantidos e aplicados sobre
                      o novo cronograma.
                    </li>
                    <li>
                      A curva será regenerada; até lá, a curva anterior continua
                      sendo exibida como desatualizada.
                    </li>
                  </ul>
                ) : (
                  <ul>
                    <li>
                      A curva será regenerada; até lá, a curva anterior continua
                      sendo exibida como desatualizada.
                    </li>
                    <li>
                      As regras, os grupos e os ajustes de mês já criados são
                      mantidos e aplicados sobre a nova origem.
                    </li>
                    <li>
                      Ajustes que não couberem na nova origem aparecerão como
                      excedentes.
                    </li>
                  </ul>
                )}
                {error ? (
                  <p className="error-message" role="alert">
                    {error}
                  </p>
                ) : null}
                <div className="ampli-form-actions">
                  <button
                    type="button"
                    className="button button-ghost small"
                    onClick={cancel}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className={`button small ${reset ? "button-error" : "button-accent"}`}
                    onClick={confirm}
                    disabled={saving}
                  >
                    {confirmLabel}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </details>
      ) : null}
      {/* Fora do recolhível: por que o ERP não foi usado é informação, não opção. */}
      {fallbackReason ? (
        <p className="ampli-origin__fallback" data-testid="origin-fallback">
          {`O desembolso do ERP não foi usado: ${reasonText(fallbackReason)}`}
        </p>
      ) : null}
    </section>
  );
};

export default OriginSelector;
